/** @jsxImportSource @emotion/react */
import { useSession } from '@quickbit/backoffice-core-frontend';
import {
  Card,
  List,
  Modal,
  Snackbar,
  Typography,
  useTheme,
  UtilityButton,
} from '@quickbit/qb-design-system';
import { useMemo, useState } from 'react';
import { useNavigate, Route, Routes } from 'react-router-dom';
import {
  useApproveRecipient,
  useRemoveRecipient,
  useGetRecipients,
} from 'api/walletRecipientApi';
import { QBLoader } from 'components';
import { useOrganisation } from 'contexts';
import { AddRecipient } from './components/AddRecipient';
import { RecipientListItem } from './components/RecipientListItem';
import { TravelRuleInfo } from './components/TravelRuleInfo';

export const Recipients = () => {
  const session = useSession();
  const { sizing, spacing } = useTheme();
  const navigate = useNavigate();
  const [errorInApproving, setErrorInApproving] = useState(false);
  const [errorInGetRecipients, setErrorInGetRecipients] = useState(false);
  const [errorInRemoving, setErrorInRemoving] = useState(false);
  const {
    selectedMerchant: { merchantId },
  } = useOrganisation();
  const {
    data: recipientsData,
    isLoading: isLoadingRecipients,
    error: getRecipientsError,
  } = useGetRecipients({ merchantId, currency: 'USDC' });

  const {
    mutateAsync: approveRecipientFn,
    isPending: approveRecipientLoading,
    error: approveRecipientError,
  } = useApproveRecipient(merchantId);
  const [deleteContactId, setDeleteContactId] = useState<string>();

  const {
    mutateAsync: removeRecipientFn,
    isPending: removeRecipientLoading,
    error: removeRecipientError,
  } = useRemoveRecipient(merchantId);

  const pendingRecipients = useMemo(
    () =>
      recipientsData?.filter((recipient) => recipient.approvedBy === null) ||
      [],
    [recipientsData]
  );
  const approvedRecipients = useMemo(
    () =>
      recipientsData?.filter((recipient) => recipient.approvedBy !== null) ||
      [],
    [recipientsData]
  );

  const showActionButtons =
    session.authenticated &&
    session.permissions.includes('edit_corporate_wallet');

  if (isLoadingRecipients) return <QBLoader />;

  const isAuthorizedToSendCrypto =
    session.authenticated &&
    session.permissions.includes('edit_corporate_wallet');

  const noRecipientText = `No recipients to show.
  ${
    isAuthorizedToSendCrypto
      ? 'Use the button above to add a new recipient.'
      : ''
  }`;

  return (
    <>
      <div css={{ maxWidth: sizing.large }}>
        <Card>
          <div
            css={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="subtitle">Recipients</Typography>
            <div>
              <UtilityButton
                text="Add recipient"
                disabled={!isAuthorizedToSendCrypto}
                onClick={() => navigate('add')}
              />
            </div>
          </div>
        </Card>
        <div
          css={{ display: 'grid', gap: spacing.xxs, marginTop: spacing.xxs }}
        >
          {recipientsData && recipientsData.length === 0 && (
            <div
              css={{
                textAlign: 'center',
                minHeight: spacing.xl,
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <Typography variant="body">{noRecipientText}</Typography>
            </div>
          )}
          {pendingRecipients.length > 0 && (
            <>
              <Typography variant="subtitle">Waiting for approval</Typography>
              <List>
                {pendingRecipients.map((recipient) => (
                  <RecipientListItem
                    key={recipient.uuid}
                    recipient={recipient}
                    approveRecipientFn={approveRecipientFn}
                    approveRecipientLoading={approveRecipientLoading}
                    removeRecipientLoading={removeRecipientLoading}
                    showActionButtons={showActionButtons}
                    setDeleteContactId={setDeleteContactId}
                  />
                ))}
              </List>
            </>
          )}
          {approvedRecipients.length > 0 && (
            <>
              <Typography variant="subtitle">Approved</Typography>
              <List>
                {approvedRecipients.map((recipient) => (
                  <RecipientListItem
                    key={recipient.uuid}
                    recipient={recipient}
                    approveRecipientFn={approveRecipientFn}
                    approveRecipientLoading={approveRecipientLoading}
                    removeRecipientLoading={removeRecipientLoading}
                    showActionButtons={showActionButtons}
                    setDeleteContactId={setDeleteContactId}
                  />
                ))}
              </List>
            </>
          )}
          {deleteContactId && (
            <Modal
              title="Delete recipient"
              subtitle="Are you sure you want to delete this recipient?"
              closeModal={() => {
                setDeleteContactId(undefined);
              }}
              primaryButtonProps={{
                text: 'Delete',
                onClick: async () => {
                  await removeRecipientFn({
                    contactId: deleteContactId,
                  });
                  setDeleteContactId(undefined);
                },
                loading: removeRecipientLoading,
                disabled: removeRecipientLoading,
              }}
              secondaryButtonProps={{
                text: 'Cancel',
                onClick: () => {
                  setDeleteContactId(undefined);
                },
              }}
            />
          )}
        </div>
      </div>
      {approveRecipientError && (
        <Snackbar
          variant="danger"
          open={errorInApproving}
          onClose={() => {
            setErrorInApproving(false);
          }}
          message={approveRecipientError.errorMessage}
        />
      )}
      {removeRecipientError && (
        <Snackbar
          variant="danger"
          open={errorInRemoving}
          onClose={() => {
            setErrorInRemoving(false);
          }}
          message={removeRecipientError.errorMessage}
        />
      )}
      {getRecipientsError && (
        <Snackbar
          variant="danger"
          open={errorInGetRecipients}
          onClose={() => {
            setErrorInGetRecipients(false);
          }}
          message={getRecipientsError.errorMessage}
        />
      )}
      <Routes>
        <Route
          path="add/travel-rule-info/:contactId"
          element={<TravelRuleInfo />}
        />
        <Route path="add" element={<AddRecipient />} />
      </Routes>
    </>
  );
};
